import React, { useState } from 'react';
import { Box, Stack, Divider, Grid, Chip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { FieldArray } from 'formik';
import { Button, Typography, COLORS } from 'pvs-design-system';
import copyToClipboard from 'utils/copyToClipboard';
import LocationSelectionModal from 'components/LocationSelectionModal/LocationSelectionModal';
import generateUUID from '../../utils/generateUUID';

const ADD_ICON_SX = { fontSize: '16px !important' };

export type RootLocationParam = {
  id: string;
  name: string;
  path: string;
  locationLevel: number;
};

export type DeploymentParam = {
  deploymentId: string;
  rootLocations: RootLocationParam[];
};

interface ToolDeploymentSectionProps {
  defaultDeploymentId: string;
  deployments: DeploymentParam[];
  deploymentsErrors: (string | undefined)[];
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
}

interface LocationSelectorConfig {
  existingLocations?: RootLocationParam[];
  onConfirm: (selectedLocations: RootLocationParam[]) => void;
}

const createDeployment = (): DeploymentParam => {
  const generatedDeploymentId = generateUUID();
  return {
    deploymentId: generatedDeploymentId,
    rootLocations: [],
  };
};

const ToolDeploymentSection: React.FC<ToolDeploymentSectionProps> = ({
  deployments,
  deploymentsErrors,
  setFieldValue,
  defaultDeploymentId,
}) => {
  const [locationSelectorConfig, setLocationSelectorConfig] =
    useState<LocationSelectorConfig | null>(null);

  const openLocationSelector = (config: LocationSelectorConfig) => {
    setLocationSelectorConfig(config);
  };

  const handleLocationSelectorConfirm = (
    selectedLocations: RootLocationParam[]
  ) => {
    if (locationSelectorConfig) {
      locationSelectorConfig.onConfirm(selectedLocations);
      setLocationSelectorConfig(null);
    }
  };

  const updateDeploymentLocations = (
    deploymentIndex: number,
    locations: RootLocationParam[]
  ) => {
    setFieldValue(
      `deployments.${deploymentIndex}.rootLocations`,
      locations,
      true
    );
  };

  const addDeployment = (
    locations: RootLocationParam[],
    pushDeployment: (deployment: DeploymentParam) => void
  ) => {
    const newDeployment = createDeployment();
    newDeployment.rootLocations = locations;

    pushDeployment(newDeployment);
  };

  const removeDeployment = (deploymentIndex: number) => {
    setFieldValue(
      'deployments',
      deployments.filter((_, index) => index !== deploymentIndex),
      true
    );
  };

  const removeDeploymentLocation = (
    deployment: DeploymentParam,
    deploymentIndex: number,
    locationId: string
  ) => {
    setFieldValue(
      `deployments.${deploymentIndex}.rootLocations`,
      deployment.rootLocations.filter((location) => location.id !== locationId),
      true
    );
  };

  return (
    <>
      <Stack gap="12px">
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              lineHeight: '24px',
              color: '#000000',
            }}
          >
            Deployments
          </Typography>
          <Typography
            variant="p-sm"
            sx={{
              fontSize: '12px',
              lineHeight: '16px',
              color: COLORS.gray[700],
            }}
          >
            A deployment of a tool defines the scope of contexts under which a
            tool is made available.
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: '8px',
            padding: '24px',
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray[100]}`,
          }}
        >
          <Grid container spacing={2} sx={{ marginBottom: '8px' }}>
            <Grid item xs={4}>
              <Typography variant="p-sm" sx={{ color: COLORS.gray[400] }}>
                Deployment ID
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="p-sm" sx={{ color: COLORS.gray[400] }}>
                Associated with
              </Typography>
            </Grid>
            <Grid item xs={1} />
          </Grid>

          <FieldArray
            name="deployments"
            render={(arrayHelpers) => (
              <>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  sx={{ padding: '12px 0' }}
                >
                  <Grid item xs={4}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="p-sm"
                        sx={{ color: COLORS.gray[700] }}
                      >
                        {defaultDeploymentId}
                      </Typography>
                      <IconButton
                        id={`copy-deployment-${defaultDeploymentId}`}
                        aria-label={`Copy deployment ${defaultDeploymentId}`}
                        onClick={() => copyToClipboard(defaultDeploymentId)}
                        size="small"
                        sx={{ padding: '2px' }}
                      >
                        <ContentCopyIcon
                          sx={{ color: COLORS.gray[400], fontSize: 16 }}
                        />
                      </IconButton>
                    </Stack>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="p-sm" sx={{ fontWeight: 600 }}>
                      Default
                    </Typography>
                  </Grid>
                </Grid>
                <Divider
                  aria-hidden="true"
                  sx={{ borderColor: COLORS.gray[100], opacity: 0.6 }}
                />
                <Stack>
                  {deployments.map(
                    (deployment: DeploymentParam, index: number) => (
                      <React.Fragment key={deployment.deploymentId}>
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ padding: '12px 0' }}
                        >
                          {/* Deployment ID column */}
                          <Grid item xs={4}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                variant="p-sm"
                                sx={{ color: COLORS.gray[700] }}
                              >
                                {deployment.deploymentId}
                              </Typography>
                              <IconButton
                                id={`copy-deployment-${deployment.deploymentId}`}
                                aria-label={`Copy deployment ${deployment.deploymentId}`}
                                onClick={() =>
                                  copyToClipboard(deployment.deploymentId)
                                }
                                size="small"
                                sx={{ padding: '2px' }}
                              >
                                <ContentCopyIcon
                                  sx={{ color: COLORS.gray[400], fontSize: 16 }}
                                />
                              </IconButton>
                            </Stack>
                          </Grid>

                          {/* Associated with column */}
                          <Grid item xs={7}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              useFlexGap
                              flexWrap="wrap"
                            >
                              {deployment.rootLocations.map((location) => (
                                <Chip
                                  key={`${deployment.deploymentId}-${location.id}`}
                                  label={location.name}
                                  size="small"
                                  onDelete={() =>
                                    removeDeploymentLocation(
                                      deployment,
                                      index,
                                      location.id
                                    )
                                  }
                                  sx={{
                                    gap: '4px',
                                    paddingRight: '4px',
                                    paddingLeft: '4px',
                                    backgroundColor: COLORS.gray[100],
                                    color: COLORS.gray[700],
                                    '& .MuiChip-deleteIcon': {
                                      color: COLORS.white,
                                    },
                                  }}
                                />
                              ))}
                              <Button
                                id={`add-location-root-${deployment.deploymentId}`}
                                startIcon={<AddIcon sx={ADD_ICON_SX} />}
                                variant="text"
                                size="medium"
                                onClick={() =>
                                  openLocationSelector({
                                    existingLocations: deployment.rootLocations,
                                    onConfirm: (locations) => {
                                      updateDeploymentLocations(
                                        index,
                                        locations
                                      );
                                    },
                                  })
                                }
                                sx={{
                                  width: 'fit-content',
                                  minWidth: 'unset',
                                  fontWeight: 600,
                                  color: '#722362',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  padding: '4px 8px',
                                }}
                              >
                                Add location root
                              </Button>
                            </Stack>
                          </Grid>

                          {/* Delete column */}
                          <Grid item xs={1}>
                            <IconButton
                              id={`delete-deployment-${deployment.deploymentId}`}
                              aria-label="Delete deployment"
                              onClick={() => removeDeployment(index)}
                              sx={{ color: COLORS.purple[700] }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>

                        {deploymentsErrors[index] && (
                          <Grid container spacing={2}>
                            <Grid item xs={4} />
                            <Grid item xs={8}>
                              <Typography
                                variant="p-sm"
                                sx={{
                                  color: COLORS.red[800],
                                  marginBottom: '8px',
                                }}
                              >
                                {deploymentsErrors[index]}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                        {index < deployments.length - 1 && (
                          <Divider
                            aria-hidden="true"
                            sx={{ borderColor: COLORS.gray[100], opacity: 0.6 }}
                          />
                        )}
                      </React.Fragment>
                    )
                  )}

                  <Box sx={{ mt: 1 }}>
                    <Button
                      id="addDeploymentButton"
                      startIcon={<AddIcon sx={ADD_ICON_SX} />}
                      onClick={() =>
                        openLocationSelector({
                          onConfirm: (locations) =>
                            addDeployment(locations, arrayHelpers.push),
                        })
                      }
                      onFocusVisible={() => {}}
                      size="medium"
                      variant="text"
                      sx={{
                        width: 'fit-content',
                        fontWeight: 600,
                        color: '#722362',
                        padding: '4px 8px',
                        minWidth: 'unset',
                      }}
                    >
                      Add deployment
                    </Button>
                  </Box>
                </Stack>
              </>
            )}
          />
        </Box>
        <Divider aria-hidden="true" flexItem />
      </Stack>

      {/* Location picker dialog */}
      {locationSelectorConfig && (
        <LocationSelectionModal
          isOpen
          onClose={() => setLocationSelectorConfig(null)}
          onConfirm={handleLocationSelectorConfirm}
          existingLocations={locationSelectorConfig.existingLocations}
        />
      )}
    </>
  );
};

export default ToolDeploymentSection;
